import { toast } from 'react-toastify';
import { SpaceErrorCode } from 'shared';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { useAppMutation } from '../use-app-mutation.hook';
import { useInvalidateQueries } from '../use-invalidate-queries.hook';
import { useLocale } from '../use-locale.hook';
import { useSelectedCompany } from '../use-selected-company.hook';
import { CreateDataRoomDto, DataRoomDetail } from './space.types';
import { SPACE_INVALIDATION_KEYS } from './space-invalidation.const';

type Props = {
  /** The name is already in use in this company — the field says so, rather than a toast. */
  onDuplicateName?: () => void;
};

export const useCreateDataRoom = ({ onDuplicateName }: Props = {}) => {
  // The route is scoped by the session's selected company on the server; the key carries it so
  // one company's create is not the same mutation as another's.
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id ?? '';
  const { messagesLocale } = useLocale();
  const { invalidateQuery } = useInvalidateQueries(...SPACE_INVALIDATION_KEYS);

  const {
    isPending,
    mutate: createDataRoom,
    mutateAsync: createDataRoomAsync,
  } = useAppMutation<DataRoomDetail, HttpException, CreateDataRoomDto>(
    [QueryKey.CREATE_DATA_ROOM, companyId],
    {
      /**
       * Replaced, the way the workspace create does it: a taken name belongs under the field
       * that holds it. Left to the default toast it flashed up and away again on every press of
       * Create, which is the one thing a person in that position does repeatedly.
       */
      defaultErrorHandling: false,
      mutationFn: (body) => ApiService.post({ endpoint: BackendRoute.DATA_ROOMS }, { body }),
      onError: (error) => {
        if (error.body?.code === SpaceErrorCode.DATA_ROOM_NAME_TAKEN) {
          onDuplicateName?.();

          return;
        }

        toast.error(error.body?.message || messagesLocale['FAILED_TO_SAVE_CHANGES']);
      },
      onSuccess: () => {
        invalidateQuery();
      },
    },
  );

  return { createDataRoom, createDataRoomAsync, isPending };
};
